import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Country } from '@/models/country';
import type { Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import type { ProgressionAward } from '@/models/scoring';
import type { TeamDetailOptions } from './teamDetailUtils';

export type TeamDetailState = {
  country: Country;
  options: TeamDetailOptions;
} | null;

type TeamDetailContextValue = {
  state: TeamDetailState;
  openTeamDetail: (country: Country, options?: TeamDetailOptions) => void;
  closeTeamDetail: () => void;
  fixtures: Fixture[];
  gameResults: gameResult[];
  progressionAwards: ProgressionAward[];
  countryList: Country[];
  pickRatesByCountry: Partial<Record<Country, number>> | undefined;
};

const TeamDetailContext = createContext<TeamDetailContextValue | null>(null);

export function TeamDetailProvider(props: {
  children: ReactNode;
  fixtures: Fixture[];
  gameResults?: gameResult[];
  progressionAwards?: ProgressionAward[];
  countryList?: Country[];
  pickRatesByCountry?: Partial<Record<Country, number>>;
}) {
  const [state, setState] = useState<TeamDetailState>(null);

  const openTeamDetail = useCallback((country: Country, options?: TeamDetailOptions) => {
    setState({ country, options: options ?? {} });
  }, []);

  const closeTeamDetail = useCallback(() => {
    setState((current) => {
      const resume = current?.options.onCloseResume;
      if (resume) {
        queueMicrotask(resume);
      }
      return null;
    });
  }, []);

  const value = useMemo(
    () => ({
      state,
      openTeamDetail,
      closeTeamDetail,
      fixtures: props.fixtures,
      gameResults: props.gameResults ?? [],
      progressionAwards: props.progressionAwards ?? [],
      countryList: props.countryList ?? [],
      pickRatesByCountry: props.pickRatesByCountry,
    }),
    [
      state,
      openTeamDetail,
      closeTeamDetail,
      props.fixtures,
      props.gameResults,
      props.progressionAwards,
      props.countryList,
      props.pickRatesByCountry,
    ],
  );

  return <TeamDetailContext.Provider value={value}>{props.children}</TeamDetailContext.Provider>;
}

export function useTeamDetail(): TeamDetailContextValue {
  const ctx = useContext(TeamDetailContext);
  if (!ctx) {
    throw new Error('useTeamDetail must be used within TeamDetailProvider');
  }
  return ctx;
}

/** Safe when provider may be absent (e.g. Storybook). */
export function useTeamDetailOptional(): TeamDetailContextValue | null {
  return useContext(TeamDetailContext);
}
